import React from 'react'
import ReactDOM from 'react-dom'
import { BrowserRouter as Router } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from 'react-query'
import { Provider as ReduxProvider } from 'react-redux'
import * as Sentry from '@sentry/react'
import { store } from 'store'
import '@crossfit/ui/dist/style.css'

import 'styles/main.css'

import App from 'components/App'
import { AuthProvider } from 'components/Auth'
import { NotificationProvider } from 'components/Notification'
import { SettingsProvider } from 'components/Settings'
import { AppProvider } from 'contexts/App'
import { DirectoryPageProvider } from 'contexts/DirectoryPage'

const PUBLIC_PATH = import.meta.env.VITE_PUBLIC_PATH
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN
const SENTRY_ENV = import.meta.env.VITE_SENTRY_ENV

if (SENTRY_DSN && SENTRY_ENV) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENV,
    release: import.meta.env.PACKAGE_VERSION,
  })
}

declare global {
  interface Window {
    Cypress: unknown
    needsToTrackScrollDepth: boolean
    needsToIdentify: boolean
  }
}

async function startMockServer() {
  if (window.Cypress) {
    await import('./mocks/browser').then(({ worker }) => {
      worker.start({
        serviceWorker: {
          url: '/tools/mockServiceWorker.js',
        },
      })
    })
  }
  return Promise.resolve()
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: false,
      staleTime: 1000 * 60 * 60 * 24,
    },
  },
})

startMockServer().then(() => {
  ReactDOM.render(
    <Router basename={PUBLIC_PATH || '/'}>
      <QueryClientProvider client={queryClient}>
        <ReduxProvider store={store}>
          <AuthProvider>
            <NotificationProvider>
              <SettingsProvider>
                <AppProvider>
                  <DirectoryPageProvider>
                    <App />
                  </DirectoryPageProvider>
                </AppProvider>
              </SettingsProvider>
            </NotificationProvider>
          </AuthProvider>
        </ReduxProvider>
      </QueryClientProvider>
    </Router>,
    document.getElementById('app'),
  )
})
